import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Eye, Truck, Printer, Loader2, RefreshCw, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiUrl } from '@/utils/api';

const API = getApiUrl();

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/orders/${orderId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status atualizado!');
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status });
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const checkPaymentStatus = async (orderId) => {
    setCheckingPayment(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/check-payment/${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Pagamento: ${response.data.mp_status}`);
        fetchOrders();
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder({ 
            ...selectedOrder, 
            status: response.data.order_status,
            payment_status: response.data.mp_status 
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao verificar pagamento');
    } finally {
      setCheckingPayment(false);
    }
  };

  const updateShipping = async (orderId, trackingCode, shippingMethod) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/orders/${orderId}`, {
        tracking_code: trackingCode,
        shipping_method: shippingMethod,
        status: 'shipped'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Informações de envio atualizadas!');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Erro ao atualizar envio');
    }
  };

  const generateShippingLabel = async (order) => {
    setGeneratingLabel(true);
    try {
      // Calculate products data
      const products = order.items.map(item => ({
        quantity: item.quantity,
        weight: 0.3 // Default weight per t-shirt
      }));

      const response = await axios.post(`${API}/shipping/create-label`, {
        order_id: order.id,
        service_id: order.shipping_service_id,
        customer_name: order.customer_name,
        customer_email: order.customer_email,
        customer_phone: order.customer_phone,
        customer_document: order.customer_document || '',
        customer_address: order.customer_address,
        customer_number: order.customer_number || 'S/N',
        customer_complement: order.customer_complement || '',
        customer_neighborhood: order.customer_neighborhood || '',
        customer_city: order.customer_city || '',
        customer_state: order.customer_state || '',
        customer_postal_code: order.customer_postal_code || '',
        products: products
      });

      if (response.data.success) {
        toast.success('Etiqueta gerada com sucesso!');
        fetchOrders();
        // Update selected order with shipment info
        setSelectedOrder({
          ...order,
          shipment: {
            melhor_envio_id: response.data.shipment_id,
            print_url: response.data.print_url
          }
        });
      }
    } catch (error) {
      console.error('Label generation error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar etiqueta');
    } finally {
      setGeneratingLabel(false);
    }
  };

  const generateCorreiosLabel = async (order) => {
    // Open PPN Web (Pré-Postagem) portal for manual label generation
    // Until the direct API integration is resolved
    const ppnUrl = 'https://prepostagem.correios.com.br';
    
    // Show info toast with order details to help manual entry
    toast.info(
      `Abrindo portal PPN dos Correios.\n\nDados do pedido:\n` +
      `Cliente: ${order.customer_name}\n` +
      `CEP: ${order.customer_postal_code}\n` +
      `Endereço: ${order.customer_address}, ${order.customer_number}`,
      { duration: 8000 }
    );
    
    window.open(ppnUrl, '_blank');
  };

  const getCorreiosLabelPdf = async (order) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API}/shipping/correios/label/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.label_url) {
        window.open(response.data.label_url, '_blank');
      } else {
        toast.error('URL da etiqueta não disponível');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao obter etiqueta');
    }
  };

  const openLabelPrint = async (order) => {
    if (order.shipment?.print_url) {
      window.open(order.shipment.print_url, '_blank');
      return;
    }
    
    // Try to fetch the label URL
    try {
      const response = await axios.get(`${API}/admin/orders/${order.id}/label`);
      if (response.data.print_url) {
        window.open(response.data.print_url, '_blank');
      } else {
        toast.error('URL da etiqueta não disponível');
      }
    } catch (error) {
      toast.error('Erro ao buscar etiqueta');
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'paid': return 'bg-green-500/20 text-green-500';
      case 'processing': return 'bg-blue-500/20 text-blue-500';
      case 'shipped': return 'bg-purple-500/20 text-purple-500';
      case 'completed': return 'bg-emerald-500/20 text-emerald-500';
      case 'cancelled': return 'bg-red-500/20 text-red-500';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Aguardando Pagamento',
      paid: 'Pago - Aguardando Envio',
      processing: 'Processando',
      shipped: 'Enviado',
      completed: 'Entregue',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const getPaymentStatusIcon = (paymentStatus) => {
    switch(paymentStatus) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'rejected': 
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-orders-page">
        <h1 className="font-heading text-4xl font-bold text-white mb-8">Pedidos</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'pending', 'paid', 'shipped', 'completed'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
                filterStatus === status
                  ? 'bg-primary text-black font-bold'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-white/10'
              }`}
            >
              {status === 'all' ? 'Todos' : getStatusLabel(status)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-white/10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-sm uppercase tracking-wider text-zinc-400">ID</th>
                  <th className="text-left p-4 text-sm uppercase tracking-wider text-zinc-400">Cliente</th>
                  <th className="text-left p-4 text-sm uppercase tracking-wider text-zinc-400">Total</th>
                  <th className="text-left p-4 text-sm uppercase tracking-wider text-zinc-400">Status</th>
                  <th className="text-left p-4 text-sm uppercase tracking-wider text-zinc-400">Data</th>
                  <th className="text-right p-4 text-sm uppercase tracking-wider text-zinc-400">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-4">
                      <span className="font-mono text-xs text-zinc-400">#{order.id.slice(0, 8)}</span>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{order.customer_name}</p>
                      <p className="text-xs text-zinc-500">{order.customer_email}</p>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-primary font-bold">R$ {order.total.toFixed(2)}</span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-400 text-sm">
                      {new Date(order.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-primary hover:text-white transition-colors"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="text-center py-12 text-zinc-400">
                Nenhum pedido encontrado
              </div>
            )}
          </div>
        )}

        {/* Order Detail Modal */}
        <AnimatePresence>
          {selectedOrder && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedOrder(null)}
                className="fixed inset-0 bg-black/80 z-50"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="bg-zinc-900 border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6">
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">
                      Detalhes do Pedido #{selectedOrder.id.slice(0, 8)}
                    </h2>

                    {/* Payment Status Section */}
                    <div className="mb-6 pb-6 border-b border-white/10">
                      <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                        Status do Pagamento
                        {getPaymentStatusIcon(selectedOrder.payment_status)}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`px-3 py-1 text-sm ${getStatusColor(selectedOrder.status)}`}>
                            {getStatusLabel(selectedOrder.status)}
                          </span>
                          {selectedOrder.paid_at && (
                            <p className="text-xs text-zinc-500 mt-2">
                              Pago em: {new Date(selectedOrder.paid_at).toLocaleString('pt-BR')}
                            </p>
                          )}
                        </div>
                        {selectedOrder.status === 'pending' && (
                          <button
                            onClick={() => checkPaymentStatus(selectedOrder.id)}
                            disabled={checkingPayment}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-500 transition-colors disabled:opacity-50"
                          >
                            {checkingPayment ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                            Verificar Pagamento
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="mb-6 pb-6 border-b border-white/10">
                      <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3">Cliente</h3>
                      <p className="text-white mb-1">{selectedOrder.customer_name}</p>
                      <p className="text-sm text-zinc-400 mb-1">{selectedOrder.customer_email}</p>
                      <p className="text-sm text-zinc-400 mb-1">{selectedOrder.customer_phone}</p>
                      <p className="text-sm text-zinc-400">{selectedOrder.customer_address}</p>
                      {selectedOrder.customer_number && (
                        <p className="text-sm text-zinc-400">Nº {selectedOrder.customer_number}{selectedOrder.customer_complement ? ` - ${selectedOrder.customer_complement}` : ''}</p>
                      )}
                      {selectedOrder.customer_neighborhood && (
                        <p className="text-sm text-zinc-400">{selectedOrder.customer_neighborhood}</p>
                      )}
                      {(selectedOrder.customer_city || selectedOrder.customer_state) && (
                        <p className="text-sm text-zinc-400">{selectedOrder.customer_city}/{selectedOrder.customer_state}</p>
                      )}
                      {selectedOrder.customer_postal_code && (
                        <p className="text-sm text-zinc-400">CEP: {selectedOrder.customer_postal_code}</p>
                      )}
                    </div>

                    {/* Items */}
                    <div className="mb-6 pb-6 border-b border-white/10">
                      <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3">Itens</h3>
                      <div className="space-y-3">
                        {selectedOrder.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <div>
                              <p className="text-white">{item.product_name}</p>
                              <p className="text-xs text-zinc-500">Tamanho: {item.size} | Qtd: {item.quantity}</p>
                            </div>
                            <p className="font-mono text-primary">R$ {(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                        {selectedOrder.subtotal && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Subtotal</span>
                            <span className="font-mono">R$ {selectedOrder.subtotal.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedOrder.shipping_name && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Frete ({selectedOrder.shipping_name})</span>
                            <span className="font-mono">
                              {selectedOrder.shipping_price === 0 ? (
                                <span className="text-green-500">GRÁTIS</span>
                              ) : (
                                `R$ ${(selectedOrder.shipping_price || 0).toFixed(2)}`
                              )}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-white/10">
                          <span className="font-heading text-lg text-white">TOTAL</span>
                          <span className="font-mono text-2xl text-primary font-bold">R$ {selectedOrder.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Label Section - Only show for paid orders */}
                    {(selectedOrder.status === 'paid' || selectedOrder.status === 'shipped' || selectedOrder.status === 'completed' || selectedOrder.payment_status === 'approved') && (
                      <div className="mb-6 pb-6 border-b border-white/10">
                        <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          Etiqueta de Envio (SuperFrete)
                        </h3>

                        {selectedOrder.shipment?.print_url || selectedOrder.shipment?.superfrete_id ? (
                          <div className="space-y-3">
                            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded">
                              <p className="text-green-500 text-sm font-bold mb-2">✓ Etiqueta Gerada</p>
                              {selectedOrder.shipment?.superfrete_id && (
                                <p className="text-xs text-zinc-400 font-mono">ID: {selectedOrder.shipment.superfrete_id}</p>
                              )}
                            </div>
                            <button
                              onClick={() => openLabelPrint(selectedOrder)}
                              className="w-full bg-primary text-black font-bold py-3 uppercase hover:bg-white transition-colors flex items-center justify-center gap-2"
                            >
                              <Printer className="h-5 w-5" />
                              Imprimir Etiqueta
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="bg-zinc-800 p-4 rounded">
                              <p className="text-sm text-zinc-400 mb-2">
                                <strong>Método:</strong> {selectedOrder.shipping_name || 'A definir'}
                              </p>
                              <p className="text-sm text-zinc-400">
                                <strong>CEP Destino:</strong> {selectedOrder.customer_postal_code}
                              </p>
                            </div>
                            <button
                              onClick={() => generateShippingLabel(selectedOrder)}
                              disabled={generatingLabel}
                              className="w-full bg-blue-600 text-white font-bold py-3 uppercase hover:bg-blue-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                              {generatingLabel ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <Printer className="h-5 w-5" />
                              )}
                              {generatingLabel ? 'Gerando...' : 'Gerar Etiqueta SuperFrete'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Status Update */}
                    <div className="mb-6">
                      <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3">Atualizar Status</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {['pending', 'processing', 'shipped', 'completed', 'cancelled'].map(status => (
                          <button
                            key={status}
                            onClick={() => updateOrderStatus(selectedOrder.id, status)}
                            className={`py-2 text-sm uppercase transition-colors ${
                              selectedOrder.status === status
                                ? 'bg-primary text-black font-bold'
                                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}
                          >
                            {getStatusLabel(status)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Shipping Info */}
                    <div className="mb-6">
                      <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3">Informações de Envio</h3>
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="Código de rastreamento"
                          defaultValue={selectedOrder.tracking_code || ''}
                          id={`tracking-${selectedOrder.id}`}
                          className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                        />
                        <input
                          type="text"
                          placeholder="Método de envio (ex: Correios PAC)"
                          defaultValue={selectedOrder.shipping_method || ''}
                          id={`shipping-${selectedOrder.id}`}
                          className="w-full bg-zinc-800 border border-zinc-700 h-12 px-4 text-white"
                        />
                        <button
                          onClick={() => {
                            const tracking = document.getElementById(`tracking-${selectedOrder.id}`).value;
                            const shipping = document.getElementById(`shipping-${selectedOrder.id}`).value;
                            updateShipping(selectedOrder.id, tracking, shipping);
                          }}
                          className="w-full bg-primary text-black font-bold py-3 uppercase hover:bg-white transition-colors flex items-center justify-center gap-2"
                        >
                          <Truck className="h-5 w-5" />
                          Marcar como Enviado
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="w-full bg-zinc-800 hover:bg-zinc-700 text-white py-3 uppercase tracking-wider transition-colors"
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;